"use client";

import * as React from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

const NOTIFICATIONS_POLL_MS = 30_000;
// After an SSE drop (network blip, token expiry, server restart) wait this
// long before reconnecting with a freshly-fetched token. EventSource also
// auto-reconnects, but we recreate it manually so the URL carries a current
// token rather than the stale one baked into the original connection.
const SSE_RECONNECT_MS = 5_000;
const POPOVER_LIMIT = 10;
const ALL_PAGE_LIMIT = 20;

export function useNotifications() {
  return useQuery({
    queryKey: qk.notifications.mine,
    queryFn: async () => getSdk().notifications.list({ limit: POPOVER_LIMIT }),
    staleTime: 15_000,
    refetchInterval: NOTIFICATIONS_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useInfiniteNotifications(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: qk.notifications.all,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      getSdk().notifications.list({
        limit: ALL_PAGE_LIMIT,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.data.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

/**
 * Realtime notification stream over SSE. While mounted (and `enabled`), holds
 * one EventSource to `/me/notifications/stream` and invalidates the inbox
 * queries whenever the server pushes a "notification" frame — so the bell badge
 * and list update instantly instead of waiting for the 30s poll. The poll in
 * `useNotifications` stays on as a fallback if the stream can't connect.
 *
 * Call this ONCE per app (e.g. in the app shell), not per popover instance, to
 * avoid opening multiple streams.
 */
export function useNotificationStream(enabled = true) {
  const qc = useQueryClient();

  React.useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return;
    }

    let stopped = false;
    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const invalidate = () => {
      void qc.invalidateQueries({ queryKey: qk.notifications.mine });
      void qc.invalidateQueries({ queryKey: qk.notifications.all });
    };

    const connect = async () => {
      if (stopped) return;
      const url = await getSdk().notifications.streamUrl();
      if (stopped || !url) return;
      es = new EventSource(url);
      es.onmessage = (evt) => {
        // Heartbeats ("ping") only keep the socket warm — ignore them. Any
        // other frame means a new notification landed.
        try {
          const parsed = JSON.parse(evt.data) as { type?: string };
          if (parsed?.type === "ping") return;
        } catch {
          // Non-JSON frame — fall through and refetch to be safe.
        }
        invalidate();
      };
      es.onerror = () => {
        // EventSource fires onerror on disconnect (incl. 401 after token
        // expiry). Tear down and reconnect with a fresh token.
        es?.close();
        es = null;
        if (stopped) return;
        clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => void connect(), SSE_RECONNECT_MS);
      };
    };

    void connect();

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [enabled, qc]);
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => getSdk().notifications.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.notifications.mine });
      qc.invalidateQueries({ queryKey: qk.notifications.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => getSdk().notifications.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.notifications.mine });
      qc.invalidateQueries({ queryKey: qk.notifications.all });
    },
  });
}
